import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';

interface ProductData {
  id: string;
  name: string;
  tagline: string;
  description: string;
  longDescription: string;
  icon: React.ReactNode;
  features: { title: string; description: string }[];
  benefits: string[];
  useCases: string[];
  color: 'primary' | 'accent';
}

const productsData: Record<string, ProductData> = {
  cimmpleflow: {
    id: 'cimmpleflow',
    name: 'CimmpleFlow',
    tagline: 'Manufacturing & Logistics Made Simple',
    description: 'Complete Information on Manufacturing Parts, Planning and Logistics Evaluation.',
    longDescription: `CimmpleFlow is our flagship cloud-based ERP solution designed for manufacturing and logistics businesses of all sizes. It provides complete visibility and control over your entire business operations - anytime, anywhere.

From quotation management with VendorConnect™ to job tracking with live updates, CimmpleFlow streamlines your workflows and helps you make data-driven decisions. Our comprehensive suite of modules covers everything from purchasing and quality control to advanced accounting and document management.`,
    icon: (
      <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
      </svg>
    ),
    features: [
      { title: 'Quotations', description: 'Create, manage, and track quotations with VendorConnect™ for seamless supplier collaboration.' },
      { title: 'Order Management', description: 'Handle all your orders from creation to fulfillment with complete tracking and visibility.' },
      { title: 'Purchasing', description: 'Streamline your procurement process with automated purchase orders and supplier management.' },
      { title: 'Job Management', description: 'Track jobs in real-time with live updates, scheduling, and resource allocation.' },
      { title: 'Quality Control', description: 'Ensure product quality with live reporting, inspections, and compliance tracking.' },
      { title: 'Reporting & Analytics', description: 'Generate scheduled reports and gain insights with comprehensive business analytics.' },
      { title: 'Accounting', description: 'Advanced accounting features including invoicing, expenses, and financial reporting.' },
      { title: 'Timesheets', description: 'Track employee time with 3rd party integrations for payroll and project costing.' },
      { title: 'Document Storage', description: 'Secure document management with version control and easy access to all your files.' },
    ],
    benefits: [
      'Cloud-based access from anywhere',
      'Real-time live tracking & updates',
      'Streamlined business workflows',
      'Complete operational visibility',
      'Reduced manual data entry',
      'Improved decision making',
    ],
    useCases: [
      'Manufacturing companies',
      'Engineering firms',
      'Construction businesses',
      'Logistics & distribution',
      'Job shops & workshops',
      'Service-based businesses',
    ],
    color: 'primary',
  },
  cimmlepay: {
    id: 'cimmlepay',
    name: 'CimmplePay',
    tagline: 'Payroll Processing, Simplified',
    description: 'An intelligent payroll management solution that automates salary processing and compliance.',
    longDescription: `CimmplePay is our upcoming payroll management solution designed to take the complexity out of paying your employees. Once launched, it will handle everything from salary calculations and tax withholdings to direct deposits and year-end reporting.

With built-in tax tables that will be automatically updated, CimmplePay will ensure your payroll is always compliant with the latest regulations. The employee self-service portal will empower your team to access their information, reducing HR workload.`,
    icon: (
      <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    features: [
      { title: 'Automated Calculations', description: 'Calculate salaries, overtime, bonuses, and deductions automatically with precision.' },
      { title: 'Tax Compliance', description: 'Stay compliant with automatic tax calculations, withholdings, and filings.' },
      { title: 'Employee Self-Service', description: 'Let employees view pay stubs, update information, and access tax documents.' },
      { title: 'Direct Deposit', description: 'Process payments directly to employee bank accounts securely and on time.' },
      { title: 'Leave Management', description: 'Track vacation, sick leave, and PTO with automated accrual calculations.' },
      { title: 'Comprehensive Reporting', description: 'Generate detailed reports for payroll, taxes, labor costs, and compliance.' },
    ],
    benefits: [
      'Process payroll in minutes, not hours',
      'Achieve 100% tax compliance',
      'Eliminate manual calculation errors',
      'Ensure employees are paid on time',
      'Reduce HR administrative workload',
      'Access payroll data from anywhere',
    ],
    useCases: [
      'Small and medium businesses',
      'Growing startups',
      'Multi-state employers',
      'Companies with hourly workers',
      'Organizations with complex pay structures',
      'Businesses with remote teams',
    ],
    color: 'accent',
  },
};

const ProductDetail: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  
  if (!productId || !productsData[productId]) {
    return <Navigate to="/products" replace />;
  }

  const product = productsData[productId];

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="mb-8">
            <ol className="flex items-center gap-2 text-sm text-slate-500 dark:text-dark-400">
              <li><Link to="/" className="hover:text-slate-900 dark:hover:text-white transition-colors">Home</Link></li>
              <li>/</li>
              <li><Link to="/products" className="hover:text-slate-900 dark:hover:text-white transition-colors">Products</Link></li>
              <li>/</li>
              <li className="text-slate-900 dark:text-white font-medium">{product.name}</li>
            </ol>
          </nav>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className={`w-24 h-24 rounded-3xl ${
                product.color === 'primary' 
                  ? 'bg-primary-100 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400' 
                  : 'bg-accent-100 dark:bg-accent-500/10 text-accent-600 dark:text-accent-400'
              } flex items-center justify-center mb-8`}>
                {product.icon}
              </div>
              
              <h1 className="text-4xl md:text-5xl font-display font-bold text-slate-900 dark:text-white mb-4">
                {product.name}
              </h1>
              <p className={`text-xl font-medium mb-6 ${
                product.color === 'primary' ? 'text-primary-600 dark:text-primary-400' : 'text-accent-600 dark:text-accent-400'
              }`}>
                {product.tagline}
              </p>
              <p className="text-slate-600 dark:text-dark-300 text-lg whitespace-pre-line">
                {product.longDescription}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <Link 
                  to={product.id === 'cimmlepay' ? '/pricing?product=cimmlepay' : '/pricing'} 
                  className="btn-primary"
                >
                  View Pricing
                </Link>
                <Link to="/contact" className="btn-secondary">
                  Request Demo
                </Link>
              </div>
            </div>

            {/* Benefits Card */}
            <div className="card p-8">
              <h3 className="text-xl font-display font-bold text-slate-900 dark:text-white mb-6">Key Benefits</h3>
              <ul className="space-y-4">
                {product.benefits.map((benefit, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center ${
                      product.color === 'primary' 
                        ? 'bg-primary-100 dark:bg-primary-500/20 text-primary-600 dark:text-primary-400' 
                        : 'bg-accent-100 dark:bg-accent-500/20 text-accent-600 dark:text-accent-400'
                    }`}>
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-slate-600 dark:text-dark-300">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-slate-50 dark:bg-dark-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title">
              Powerful <span className="gradient-text">Features</span>
            </h2>
            <p className="section-subtitle mx-auto mt-4">
              Everything you need to manage your operations effectively.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {product.features.map((feature, index) => (
              <div key={index} className="card group hover:border-primary-500/30">
                <div className={`w-12 h-12 rounded-xl ${
                  product.color === 'primary' 
                    ? 'bg-primary-100 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400 group-hover:bg-primary-200 dark:group-hover:bg-primary-500/20' 
                    : 'bg-accent-100 dark:bg-accent-500/10 text-accent-600 dark:text-accent-400 group-hover:bg-accent-200 dark:group-hover:bg-accent-500/20'
                } flex items-center justify-center mb-4 transition-colors duration-300`}>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-lg font-display font-bold text-slate-900 dark:text-white mb-2">{feature.title}</h3>
                <p className="text-slate-600 dark:text-dark-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="section-title mb-6">
                Perfect for your <span className="gradient-text">industry</span>
              </h2>
              <p className="text-slate-600 dark:text-dark-300 text-lg mb-8">
                {product.name} is trusted by businesses across various industries.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {product.useCases.map((useCase, i) => (
                  <div key={i} className="flex items-center gap-3 p-4 rounded-xl bg-slate-100 dark:bg-dark-800/30">
                    <svg className={`w-5 h-5 ${
                      product.color === 'primary' ? 'text-primary-600 dark:text-primary-500' : 'text-accent-600 dark:text-accent-500'
                    }`} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-slate-700 dark:text-dark-300 text-sm">{useCase}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA Card */}
            <div className="card p-8 text-center relative overflow-hidden">
              <div className={`absolute inset-0 bg-gradient-to-br ${
                product.color === 'primary' 
                  ? 'from-primary-500/5 to-primary-500/10' 
                  : 'from-accent-500/5 to-accent-500/10'
              }`} />
              <div className="relative">
                <h3 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-4">
                  Ready to get started?
                </h3>
                <p className="text-slate-600 dark:text-dark-300 mb-8">
                  Start using {product.name} today and transform your business.
                </p>
                <Link to="/contact" className="btn-primary">
                  Request a Demo
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Other Products */}
      <section className="py-20 bg-slate-50 dark:bg-dark-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-8">
            Explore Our Other Products
          </h2>
          <Link
            to={`/products/${product.id === 'cimmpleflow' ? 'cimmlepay' : 'cimmpleflow'}`}
            className="btn-secondary inline-flex items-center gap-2"
          >
            View {product.id === 'cimmpleflow' ? 'CimmplePay' : 'CimmpleFlow'}
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;
